import { useState, useRef, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  FormField,
  Input,
  Modal,
  ProgressiveListFooter,
  Select,
} from "@playbookai/ui";
import {
  COMPACTION_TARGET_MAX_PERCENT,
  COMPACTION_TARGET_MIN_PERCENT,
  COMPACTION_TRIGGER_MAX_PERCENT,
  COMPACTION_TRIGGER_MIN_PERCENT,
  DEFAULT_COMPACTION_TARGET_PERCENT,
  DEFAULT_COMPACTION_TRIGGER_PERCENT,
  compactionTokens,
  getConnection,
  listCatalogueModels,
  providerFamily,
  saveProfile,
  type AdminConnection,
  type ExecutionProfile,
  validateCombinedBudget,
  validateCompactionThresholds,
  validateDescription,
  validateInferenceTarget,
  validateGatewayModelId,
  validateProfileName,
  validateTemperature,
  validateTokenBudget,
  validateTopP,
  type ProfileInput,
} from "../../../api/admin-ai-profiles-api.js";

/**
 * "80% · about 25,600 tokens" — the percentage is what is stored, the count is
 * what an administrator can reason about. Silent while the input budget is not
 * yet a usable number, because a derived count computed from an invalid budget
 * would be a confident wrong answer rather than a helpful one.
 */
function compactionHint(
  lead: string,
  percent: number,
  maxInput: string,
): string {
  const tokens = compactionTokens(Number(maxInput.trim()), percent);
  // locale-number: a token budget is a count.
  return tokens === null
    ? `${percent}% of the maximum input budget.`
    : `${lead} ${tokens.toLocaleString()} of the ${Number(maxInput.trim()).toLocaleString()} token input budget.`;
}

export function ProfileEditor({
  profile,
  connections,
  queryKey,
  onSaved,
  initialConnectionId,
  hasMoreConnections,
  loadingMoreConnections,
  connectionLoadError,
  onLoadMoreConnections,
  onRetryConnections,
  onClose,
}: {
  profile: ExecutionProfile | null;
  initialConnectionId?: string | undefined;
  connections: AdminConnection[];
  hasMoreConnections: boolean;
  loadingMoreConnections: boolean;
  connectionLoadError?: string | undefined;
  onLoadMoreConnections: () => void;
  onRetryConnections: () => void;
  queryKey: readonly string[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const eligible: {
    id: string;
    display_name: string;
    provider_key: string;
    provider_config?: Record<string, string>;
  }[] = connections
    .filter((row) => row.mode === "chat")
    .map(({ id, display_name, provider_key, provider_config }) => ({
      id,
      display_name,
      provider_key,
      provider_config,
    }));
  if (
    profile?.connection &&
    !eligible.some((row) => row.id === profile.connection_id)
  ) {
    eligible.unshift({
      id: profile.connection_id,
      display_name: profile.connection.display_name ?? "Current connection",
      provider_key: profile.connection.provider_key ?? "",
    });
  }
  const [connectionId, setConnectionId] = useState(
    profile?.connection_id ?? initialConnectionId ?? eligible[0]?.id ?? "",
  );
  const [effort, setEffort] = useState(
    profile?.settings.reasoning_effort ?? "",
  );
  const [temperature, setTemperature] = useState(
    String(profile?.settings.temperature ?? ""),
  );
  const [topP, setTopP] = useState(String(profile?.settings.top_p ?? ""));
  const firstInput = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [modelId, setModelId] = useState(profile?.catalogue_model_id ?? "");
  const [target, setTarget] = useState(profile?.model_or_deployment ?? "");
  const [name, setName] = useState(profile?.display_name ?? "");
  const [description, setDescription] = useState(profile?.description ?? "");
  const [maxInput, setMaxInput] = useState(
    String(profile?.settings.max_input_tokens ?? 32000),
  );
  const [maxOutput, setMaxOutput] = useState(
    String(profile?.settings.max_output_tokens ?? 4096),
  );
  /* A profile stored before the context-policy column existed carries none of
     these, and AIS defaults them the same way — so an untouched legacy profile
     saves as on/80/20 rather than as whatever the form happened to render. */
  const [autoCompaction, setAutoCompaction] = useState(
    profile?.context_policy?.auto_compaction_enabled ?? true,
  );
  const [compactTrigger, setCompactTrigger] = useState(
    profile?.context_policy?.compaction_trigger_percent ??
      DEFAULT_COMPACTION_TRIGGER_PERCENT,
  );
  const [compactTarget, setCompactTarget] = useState(
    profile?.context_policy?.compaction_target_percent ??
      DEFAULT_COMPACTION_TARGET_PERCENT,
  );
  const locked = profile?.locked ?? false;
  const catalogue = useQuery({
    queryKey: [...queryKey, "catalogue"],
    queryFn: listCatalogueModels,
  });
  const listedConnection = eligible.find((row) => row.id === connectionId);
  // The profile's connection summary omits provider_config. Fetch an off-page
  // connection before choosing gateway validation; locked renames need no read.
  const needsConnectionMetadata =
    !locked &&
    !!connectionId &&
    (!listedConnection ||
      (listedConnection.provider_key === "bedrock" &&
        !listedConnection.provider_config));
  const connectionMetadata = useQuery({
    queryKey: [...queryKey, "connection", connectionId],
    queryFn: () => getConnection(connectionId),
    enabled: needsConnectionMetadata,
  });
  const connection = needsConnectionMetadata
    ? connectionMetadata.data
    : listedConnection;
  const connectionMetadataUnavailable =
    needsConnectionMetadata &&
    (!connectionMetadata.data || connectionMetadata.isError);
  const models = (catalogue.data ?? []).filter(
    (model) =>
      model.provider_id === providerFamily(connection?.provider_key ?? ""),
  );
  const model = models.find((row) => row.id === modelId);
  const needsTarget = ["azure_openai", "azure_anthropic", "bedrock"].includes(
    model?.runtime_provider_key ?? "",
  );
  const gatewayTarget =
    model?.runtime_provider_key === "bedrock" &&
    connection?.provider_config?.endpoint_type === "custom_gateway";
  const effortInvalid = !!effort && !model?.effort_options.includes(effort);
  /* Recomputed every render from the draft, so every invalid field reports at
     once. A locked profile keeps its execution settings, so the budgets it
     cannot change must not be able to block its rename. */
  const errors = {
    name: validateProfileName(name),
    description: validateDescription(description),
    maxInput: locked
      ? null
      : (validateTokenBudget(
          maxInput,
          "Maximum input tokens",
          model?.max_input_tokens,
        ) ??
        /* The cross-field rule reports here because that is the field AIS names
           in details.field. Its own message wins while it has one. */
        validateCombinedBudget(
          maxInput,
          maxOutput,
          model?.context_window_tokens,
        )),
    /* Each of the three below replaced a native constraint. `needsTarget`
       mirrors the control's own `required`, so a hidden or optional target
       cannot block the save. */
    target: locked
      ? null
      : gatewayTarget
        ? validateGatewayModelId(target)
        : validateInferenceTarget(
            target,
            needsTarget && model?.runtime_provider_key !== "bedrock",
          ),
    /* `=== true`, not `?? true`: the flag is `boolean | null`, and
       validate_settings_against_model treats unknown support as UNSUPPORTED
       ("a control that is not verified is not exposed, and a value for it is
       not accepted"). Defaulting the null to `true` would promote unverified
       to verified and let a retained value reach AIS. This also matches the
       hint, which already reads "Unsupported" whenever the flag is not true. */
    temperature: locked
      ? null
      : validateTemperature(temperature, model?.temperature_supported === true),
    topP: locked ? null : validateTopP(topP, model?.top_p_supported === true),
    maxOutput: locked
      ? null
      : validateTokenBudget(
          maxOutput,
          "Maximum output tokens",
          model?.max_output_tokens,
        ),
    /* Checked whether or not compaction is currently ON, because AIS checks it
       unconditionally: a stored policy has to stay coherent, or switching
       compaction back on later would activate a pair nothing ever validated.
       The sliders clamp, so this is a guard rather than a routine message.
       NOT gated on `locked` — the context policy is its own edit boundary and
       stays editable on an enabled profile. */
    compaction: validateCompactionThresholds(compactTrigger, compactTarget),
  };
  const invalid = Object.values(errors).some(Boolean);
  /* See ConnectionEditor: a message waits for its field to be left, or for a
     save to be attempted. A form that objects before the administrator has
     typed anything reads as the product arguing with them. */
  /* Autofocus lands on the first control, so a blur is not on its own proof the
     administrator engaged with it - clicking straight past an untouched field
     would otherwise accuse it. A field speaks only once it has been EDITED and
     then left, or once a save is attempted. */
  const edited = useRef<Record<string, boolean>>({});
  const change = (field: string) => {
    edited.current[field] = true;
  };
  const leave = (field: string) => {
    if (edited.current[field])
      setTouched((prior) => ({ ...prior, [field]: true }));
  };
  const shown = (field: string, message: string | null) =>
    submitAttempted || touched[field] ? message : null;
  /* The combined-budget message lives on maxInput but can be broken by editing
     either budget, so leaving maxOutput is enough to reveal it. */
  const shownBudget = (message: string | null) =>
    submitAttempted || touched["maxInput"] || touched["maxOutput"]
      ? message
      : null;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (connectionMetadataUnavailable) return;
    if (invalid) {
      setSubmitAttempted(true);
      return;
    }
    setSaving(true);
    setError(null);
    const input: ProfileInput = {
      display_name: name.trim(),
      description: description.trim(),
      connection_id: connectionId,
      catalogue_model_id: modelId,
      /* Trimmed, because the validator judges the trimmed value: without this a
         whitespace-only optional target passes the form and is stored, since the
         server's min_length=1 counts three spaces as three characters. */
      model_or_deployment: target.trim() || null,
      settings: {
        max_input_tokens: Number(maxInput),
        max_output_tokens: Number(maxOutput),
        reasoning_effort: effort || null,
        temperature: temperature.trim() ? Number(temperature) : null,
        top_p: topP.trim() ? Number(topP) : null,
      },
      context_policy: {
        auto_compaction_enabled: autoCompaction,
        compaction_trigger_percent: compactTrigger,
        compaction_target_percent: compactTarget,
      },
    };
    try {
      await saveProfile(
        profile,
        /* A locked profile resends its stored EXECUTION configuration
           unchanged, so a rename cannot be refused for carrying a field the
           server would treat as an execution edit. `context_policy` is
           deliberately NOT reset here: it is its own boundary, editable at any
           lifecycle state, and the sliders stay live for exactly that reason. */
        locked && profile
          ? {
              ...input,
              connection_id: profile.connection_id,
              catalogue_model_id: profile.catalogue_model_id,
              model_or_deployment: profile.model_or_deployment,
              settings: profile.settings,
            }
          : input,
      );
      onSaved();
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not save the profile.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      initialFocusRef={firstInput}
      open
      onClose={() => {
        if (!saving) onClose();
      }}
      title={profile ? "Edit profile" : "Create profile"}
      size="md"
    >
      <form
        /* See ConnectionEditor: the app owns every message, so the browser's
           own validation must not fire first. `required`, `type` and `min`
           stay for semantics and assistive tech. */
        noValidate
        className="grid min-w-0 gap-4 [&>*]:min-w-0"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        {error && (
          <Alert tone="critical" title="Couldn't save the profile">
            {error}
          </Alert>
        )}
        {needsConnectionMetadata && connectionMetadata.isPending && (
          <p role="status">Loading connection details…</p>
        )}
        {needsConnectionMetadata && connectionMetadata.isError && (
          <Alert tone="critical" title="Couldn't load connection details">
            Check the connection details before saving this profile.
            <Button
              onClick={() => void connectionMetadata.refetch()}
              variant="secondary"
            >
              Retry connection
            </Button>
          </Alert>
        )}
        <FormField
          label="Profile name"
          hint="How this profile appears wherever a model is chosen."
          error={shown("name", errors.name)}
        >
          {/* No maxLength here either: it counts UTF-16 units. */}
          <Input
            name="name"
            required
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              change("name");
            }}
            onBlur={() => leave("name")}
            ref={firstInput}
          />
        </FormField>
        <FormField
          label="Description"
          hint="Optional. What this profile is for, for the next admin."
          error={shown("description", errors.description)}
        >
          <Input
            name="description"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              change("description");
            }}
            onBlur={() => leave("description")}
          />
        </FormField>
        <Select
          label="Connection"
          value={connectionId}
          onChange={(value) => {
            setConnectionId(value);
          }}
          options={eligible.map((row) => ({
            value: row.id,
            label: row.display_name,
          }))}
          disabled={locked}
          hint="Test this profile before enabling it. The connection must be available."
        />
        <ProgressiveListFooter
          loadedCount={eligible.length}
          itemLabel="connections"
          singularItemLabel="connection"
          loadMoreLabel="Load more connections"
          hasMore={hasMoreConnections}
          busy={loadingMoreConnections}
          error={connectionLoadError}
          onLoadMore={onLoadMoreConnections}
          onRetry={onRetryConnections}
        />
        {catalogue.error && (
          <Alert tone="critical">{catalogue.error.message}</Alert>
        )}
        <Select
          label="Model"
          value={modelId}
          onChange={setModelId}
          disabled={locked || catalogue.isPending}
          options={[
            { value: "", label: "Choose a model" },
            ...models.map((row) => ({
              value: row.id,
              label:
                row.display_name + (row.selectable ? "" : " (unavailable)"),
            })),
          ]}
        />
        {modelId && !model && (
          <Alert tone="watch">
            Choose a model compatible with this connection. Your settings have
            been retained.
          </Alert>
        )}
        {model && !model.selectable && (
          <Alert tone="watch">
            {model.unavailable_reason ?? "This model is unavailable."}
          </Alert>
        )}
        {(needsTarget || target) && (
          <FormField
            label={
              gatewayTarget
                ? "Gateway model ID"
                : model?.runtime_provider_key === "bedrock"
                  ? "Inference target (optional)"
                  : "Deployment name"
            }
            error={shown("target", errors.target)}
          >
            <Input
              value={target}
              onChange={(event) => {
                setTarget(event.target.value);
                change("target");
              }}
              onBlur={() => leave("target")}
              disabled={locked}
              required={
                gatewayTarget ||
                (needsTarget && model?.runtime_provider_key !== "bedrock")
              }
            />
          </FormField>
        )}
        {effortInvalid && (
          <Alert tone="watch">
            The selected effort is unsupported by this model. Choose another
            effort.
          </Alert>
        )}
        {locked && (
          <Alert tone="info">
            Execution settings are locked. Duplicate this profile to change its
            model or settings. Conversation context stays editable.
          </Alert>
        )}
        <Select
          label="Reasoning effort"
          value={effort}
          onChange={setEffort}
          disabled={locked || !model}
          options={[
            { value: "", label: "Provider default" },
            ...(model?.effort_options ?? []).map((value) => ({
              value,
              label: value,
            })),
          ]}
        />
        <FormField
          label="Maximum input tokens"
          hint="Maximum estimated input per request, including instructions and conversation content. Requests above this budget are rejected."
          error={shownBudget(errors.maxInput)}
        >
          <Input
            name="input"
            type="number"
            required
            min={1}
            disabled={locked}
            max={model?.max_input_tokens ?? undefined}
            value={maxInput}
            onChange={(event) => {
              setMaxInput(event.target.value);
              change("maxInput");
            }}
            onBlur={() => leave("maxInput")}
          />
        </FormField>
        <FormField
          label="Maximum output tokens"
          hint="Maximum response budget for this profile. Keep both budgets within your model’s supported limits; input and output may share a context window."
          error={shown("maxOutput", errors.maxOutput)}
        >
          <Input
            name="output"
            type="number"
            required
            min={1}
            disabled={locked}
            max={model?.max_output_tokens ?? undefined}
            value={maxOutput}
            onChange={(event) => {
              setMaxOutput(event.target.value);
              change("maxOutput");
            }}
            onBlur={() => leave("maxOutput")}
          />
        </FormField>
        <p className="text-xs text-pb-text-muted">
          {/* locale-number: model token capacity is a count. */}
          {model?.context_window_tokens
            ? `Combined input and output limit: ${model.context_window_tokens.toLocaleString()} tokens.`
            : ""}
        </p>

        {/* Conversation context — the PROFILE owns the compaction policy; each
            conversation owns its own checkpoints and progress. Placed after
            the budgets because both sliders are percentages OF the input
            budget directly above. */}
        <fieldset className="grid gap-3 rounded-md border border-pb-border p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-pb-text-muted">
            Conversation context
          </legend>
          <label className="flex items-start justify-between gap-3 text-sm text-pb-text">
            <span className="min-w-0">
              <span className="block">Automatic compaction</span>
              <span className="block text-xs text-pb-text-muted">
                Summarize older exchanges so a long conversation keeps working
                in the same thread. The full conversation history is always
                kept.
              </span>
            </span>
            {/* NOT disabled by `locked`: the context policy is its own edit
                boundary and stays editable once the profile is enabled, so an
                administrator can tune compaction without duplicating a model
                that has not changed. */}
            {/* eslint-disable-next-line ds/no-hand-built -- kept: the DS Checkbox paints its own box and mark instead of the native control; no PR 3 look row approves that. */}
            <input
              type="checkbox"
              name="autoCompaction"
              className="mt-1 shrink-0"
              checked={autoCompaction}
              onChange={(event) => setAutoCompaction(event.target.checked)}
            />
          </label>

          <FormField
            label={`Trigger at ${compactTrigger}%`}
            hint={compactionHint("Compacts at about", compactTrigger, maxInput)}
            /* Shown immediately rather than on blur: the sliders clamp, so a
               message here means the stored pair is genuinely inconsistent —
               not that the administrator has not finished typing. */
            error={errors.compaction}
          >
            {/* eslint-disable-next-line ds/no-hand-built -- kept: the DS Slider paints the track teal (accent-pb-teal) and adds its own focus ring; no PR 3 look row approves that. */}
            <input
              type="range"
              name="compactionTrigger"
              className="w-full"
              min={COMPACTION_TRIGGER_MIN_PERCENT}
              max={COMPACTION_TRIGGER_MAX_PERCENT}
              step={5}
              disabled={!autoCompaction}
              value={compactTrigger}
              onChange={(event) => {
                const next = Number(event.target.value);
                setCompactTrigger(next);
                /* Clamping here rather than only reporting keeps the invalid
                   pair unreachable: dragging the trigger below the target
                   would otherwise leave the form in a state whose only exit is
                   moving the OTHER slider. */
                setCompactTarget((current) =>
                  current >= next
                    ? Math.max(COMPACTION_TARGET_MIN_PERCENT, next - 5)
                    : current,
                );
              }}
            />
          </FormField>

          <FormField
            label={`Compact down to ${compactTarget}%`}
            hint={compactionHint("Targets about", compactTarget, maxInput)}
          >
            {/* eslint-disable-next-line ds/no-hand-built -- kept: the DS Slider paints the track teal (accent-pb-teal) and adds its own focus ring; no PR 3 look row approves that. */}
            <input
              type="range"
              name="compactionTarget"
              className="w-full"
              min={COMPACTION_TARGET_MIN_PERCENT}
              max={COMPACTION_TARGET_MAX_PERCENT}
              step={5}
              disabled={!autoCompaction}
              value={compactTarget}
              onChange={(event) => {
                const next = Number(event.target.value);
                setCompactTarget(
                  Math.min(
                    next,
                    Math.max(COMPACTION_TARGET_MIN_PERCENT, compactTrigger - 5),
                  ),
                );
              }}
            />
          </FormField>
          <p className="text-xs text-pb-text-muted">
            The target is best-effort. Required instructions, the current
            request, recent exchanges and anything still awaiting approval are
            never dropped to reach it, and the maximum input budget above stays
            a hard limit either way.
          </p>
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          {(model?.temperature_supported || temperature !== "") && (
            <FormField
              label="Temperature (optional)"
              hint={
                model?.temperature_supported
                  ? undefined
                  : "Unsupported: clear this value before saving."
              }
              error={shown("temperature", errors.temperature)}
            >
              <Input
                name="temperature"
                type="number"
                min={0}
                max={2}
                step="any"
                disabled={locked}
                value={temperature}
                onChange={(event) => {
                  setTemperature(event.target.value);
                  change("temperature");
                }}
                onBlur={() => leave("temperature")}
              />
            </FormField>
          )}
          {(model?.top_p_supported || topP !== "") && (
            <FormField
              label="Top P (optional)"
              hint={
                model?.top_p_supported
                  ? undefined
                  : "Unsupported: clear this value before saving."
              }
              error={shown("topP", errors.topP)}
            >
              <Input
                name="topP"
                type="number"
                min={0.000001}
                max={1}
                step="any"
                disabled={locked}
                value={topP}
                onChange={(event) => {
                  setTopP(event.target.value);
                  change("topP");
                }}
                onBlur={() => leave("topP")}
              />
            </FormField>
          )}
        </div>
        <p className="text-xs text-pb-text-muted">
          {profile
            ? "Execution settings lock permanently when the profile is first enabled."
            : "New profiles start as drafts. Test this profile, then enable it."}{" "}
          Unsupported settings are rejected by AIS.
        </p>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              saving ||
              connectionMetadataUnavailable ||
              (submitAttempted && invalid) ||
              !connectionId ||
              (!locked &&
                (!model?.selectable ||
                  effortInvalid ||
                  catalogue.isPending ||
                  !!catalogue.error))
            }
          >
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
